<script lang="ts" setup>
import type { ChapterConfig } from '../configs/types'
import { getChapterConfig } from '../configs/brands'
import { getDailyPrice, getSavePercent, getUnitPriceValue, shouldShowDailyPrice, shouldShowPreferentialTotal } from '../utils/subscribeDailyPrice'
import { VIP_TYPE } from '@/constants'
import { useI18n } from '@/composables/useI18n'

const props = defineProps<{
  item: any
  getUnitPrice: (item: any) => string | number
  isZero: (value: any) => boolean
}>()

const { t } = useI18n()

// 加载当前品牌的 chapter 配置
const config: ChapterConfig = getChapterConfig()

/**
 * 获取卡片类型
 * discount: 折扣会员
 * preferential: 畅读会员有优惠价
 * normal: 畅读会员无优惠价或免费
 */
function getCardType(): 'discount' | 'preferential' | 'normal' {
  if (props.item.subscribeType === VIP_TYPE.DISCOUNT)
    return 'discount'

  // 畅读会员
  if (props.item.preferentialPrice !== undefined && props.item.preferentialPrice !== null && !props.isZero(props.item.preferentialPrice))
    return 'preferential'

  return 'normal'
}
</script>

<template>
  <div
    class="subscribe-item-novelarc text-left"
    :class="[`card-${getCardType()}`, { 'has-tag': item.cornerTip === 1 && item.marketingCopy }]"
  >
    <!-- Popular Tag -->
    <div v-if="item.cornerTip === 1 && item.marketingCopy" class="popular-tag">
      {{ item.marketingCopy }}
    </div>

    <!-- 折扣会员 -->
    <template v-if="item.subscribeType === VIP_TYPE.DISCOUNT">
      <!-- 周期标签 -->
      <div class="cycle-tag">
        {{ item.cycleStr }}
      </div>

      <div class="card-content">
        <div class="card-left">
          <div class="coins-text">
            {{ item.discountCoins }} {{ t('coins') }}
          </div>
          <ul class="desc-list">
            <li>
              <BasicFitText>{{ t('SET', { 0: item.discountOnceCoins }) }}</BasicFitText>
            </li>
            <li>
              <BasicFitText>{{ t('SET2', { 0: item.discountBonusCoins, 1: item.discountDailyCoins }) }}</BasicFitText>
            </li>
          </ul>
        </div>
        <div class="card-right">
          <div class="price-btn price-btn-discount">
            ${{ item.preferentialPrice || item.price }}
          </div>
          <div class="price-original price-original-discount">
            $ {{ item.discountOriginalPrice }}
          </div>
        </div>
      </div>
    </template>

    <!-- 畅读会员 - 有优惠价 -->
    <template v-else-if="getCardType() === 'preferential'">
      <div class="card-content">
        <div class="card-left">
          <div class="title-row">
            <BasicFitText class="title-text title-text-preferential">
              {{ item.cycleStr }}
            </BasicFitText>
            <div v-if="shouldShowDailyPrice(item, isZero)" class="save-tag">
              SAVE {{ getSavePercent(item) }}%
            </div>
          </div>
          <!-- 覆写文案(ZKSN-251):一行放得下则单行展示,放不下按 \n 拆行并逐行缩放 -->
          <div class="desc-text desc-text-preferential">
            <BasicFitLines v-if="item.subscribeTextOverridden" :text="item.subscribeText" />
            <template v-else>
              {{ item.subscribeText }}
            </template>
          </div>
        </div>
        <div class="card-right">
          <div class="price-btn price-btn-preferential">
            <template v-if="shouldShowDailyPrice(item, isZero)">
              ${{ getDailyPrice(item) }}<span class="price-unit-suffix"> / Day</span>
            </template>
            <template v-else>
              ${{ item.preferentialPrice }}
            </template>
          </div>
          <div class="price-unit price-unit-preferential">
            <span class="price-old">$ {{ item.price }}</span>
            <span v-if="shouldShowPreferentialTotal(item, isZero)" class="price-actual">$ {{ item.preferentialPrice }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- 畅读会员 - 无优惠价或免费 -->
    <template v-else>
      <div class="card-content">
        <div class="card-left">
          <BasicFitText class="title-text title-text-normal">
            {{ item.cycleStr }}
          </BasicFitText>
          <div class="desc-text desc-text-normal">
            <BasicFitLines v-if="item.subscribeTextOverridden" :text="item.subscribeText" />
            <template v-else>
              {{ item.subscribeText }}
            </template>
          </div>
        </div>
        <div class="card-right">
          <div class="price-btn price-btn-normal">
            <template v-if="isZero(item.preferentialPrice)">
              {{ config.texts.payTemplateFree }}
            </template>
            <template v-else-if="getUnitPriceValue(item)">
              ${{ getUnitPriceValue(item) }}<span class="price-unit-suffix"> / Day</span>
            </template>
            <template v-else>
              ${{ item.price }}
            </template>
          </div>
          <div v-if="!isZero(item.preferentialPrice) && getUnitPriceValue(item)" class="price-unit price-unit-normal">
            $ {{ item.price }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.subscribe-item-novelarc {
  position: relative;
  padding: 8px 14px;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  min-height: 91PX;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;

  // 有顶部角标时下移内容，避免金币文字与角标贴得过近
  &.has-tag {
    padding-top: 20px;
  }

  // 折扣会员样式
  &.card-discount {
    background: linear-gradient(270deg, #FFECDE 0%, #FFDFB8 100%);
  }

  // 畅读会员有优惠价样式
  &.card-preferential {
    background: linear-gradient(270deg, #FFE1E4 0%, #FFD9DF 100%);
  }

  // 畅读会员无优惠价或免费样式
  &.card-normal {
    background: linear-gradient(270deg, #E9EBFE 0%, #DEE2FE 100%);
  }
}

.popular-tag {
  position: absolute;
  top: 0;
  left: 0;
  height: 16px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  background: linear-gradient(90deg, #FF4754 0%, #FEA94C 100%);
  border-radius: 8px 100px 100px 0;
  font-weight: 500;
  font-size: 10px;
  color: #fff;
}

// 折扣会员周期标签（仅折扣会员使用）
.cycle-tag {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 8px 0 8px;
  font-size: 10px;
  background: linear-gradient(270deg, #FFD1AE 0%, #FFD5A2 100%);
  color: #775217;
  opacity: 0.9;
  padding: 1px 10px;
}

.card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-left {
  flex: 1;
  min-width: 0;
}

.card-right {
  flex-shrink: 0;
  text-align: right;
}

// 折扣会员文字样式
.coins-text {
  font-weight: 500;
  font-size: 18px;
  color: #775217;
}

.desc-list {
  margin-top: 4px;
  padding-left: 16px;
  font-size: 11px;
  color: #9E7739;

  li {
    list-style: disc;
    margin-top: 2px;
  }
}

// 畅读会员文字样式
.title-text {
  font-weight: 500;
  font-size: 18px;

  &.title-text-preferential {
    color: #77382C;
  }

  &.title-text-normal {
    color: #3D426E;
  }
}

.desc-text {
  margin-top: 4px;

  &.desc-text-preferential {
    font-size: 11px;
    color: #A96D61;
  }

  &.desc-text-normal {
    font-size: 14px;
    color: #797CA0;
  }
}

// 价格按钮样式
.price-btn {
  width: 81px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  font-size: 14px;
  color: #FFFFFF;

  &.price-btn-discount {
    background: linear-gradient(90deg, #F7A82A 0%, #FE7E01 100%);
  }

  &.price-btn-preferential {
    background: linear-gradient(90deg, #FF7FED 0%, #FF516B 100%);
  }

  &.price-btn-normal {
    background: linear-gradient(90deg, #C77CFF 0%, #6773F3 100%);
  }
}

// 原价/划线价样式
.price-original {
  margin-top: 3px;
  font-size: 11px;
  text-decoration-line: line-through;
  text-align: center;

  &.price-original-discount {
    color: #9E7739;
  }
}

.price-unit {
  margin-top: 4px;
  font-size: 12px;
  text-align: center;

  &.price-unit-preferential {
    color: #A96D61;
  }

  &.price-unit-normal {
    color: #797CA0;
  }
}

// 划线原价（仅作用于原价文字，避免连带划掉并排的实付价）
.price-old {
  text-decoration-line: line-through;
}

// 实付优惠价（在划线原价旁突出展示，对齐 rocnovel 版式）
.price-actual {
  margin-left: 4px;
  font-weight: 500;
  color: #77382C;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.save-tag {
  flex-shrink: 0;
  background: rgba(247, 73, 132, 0.15);
  color: rgba(247, 73, 132, 1);
  font-size: 10px;
  font-weight: 500;
  border-radius: 4px;
  padding: 3px 6px 4px 6px;
  line-height: 8px;
}

.price-unit-suffix {
  font-size: 12px;
  font-weight: normal;
  // 用白色 + 透明度，替代原先偏灰看不清的继承色
  color: #fff;
  opacity: 0.85;
}
</style>
